import { ProjectStateAsString } from 'controllers/Project/ProjectStateMachine'
import { useClientStorage } from 'customHooks/useClientStorage'
import { isEqual, uniq } from 'lodash'
import React, { useCallback } from 'react'
import { ProjectFilterContext } from './ProjectFilterContext'

export type ProjectFilterContextType = {
  onlyMyProjects: boolean
  setOnlyMyProjects: (value: boolean) => void
  showCanceledProjects: boolean
  setShowCanceledProjects: (value: boolean) => void
  onlyCreatedInPortal: boolean
  setOnlyCreatedInPortal: (value: boolean) => void
  currentQuery: string
  setCurrentQuery: (value: string) => void
  fromDate: Date
  setFromDate: (value: Date) => void
  toDate: Date
  setToDate: (value: Date) => void
  projectStateFilters: ProjectStateAsString[]
  handleProjectStateFilter: (
    selectedProjectStates: ProjectStateAsString[],
  ) => void
  validateDateRange: (from: Date, to: Date) => boolean
}

export const ProjectFilterProvider = (props: { children: React.ReactNode }) => {
  const [onlyMyProjects, setOnlyMyProjects] = useClientStorage<boolean>(
    'filterMyProjects',
    false,
  )
  const [showCanceledProjects, _setShowCanceledProjects] =
    useClientStorage<boolean>('showCanceledProjects', false)

  const [onlyCreatedInPortal, setOnlyCreatedInPortal] =
    useClientStorage<boolean>('onlyCreatedInPortal', false)

  const [currentQuery, setCurrentQuery] = useClientStorage('currentQuery', '')

  const [fromDate, setFromDate] = useClientStorage<Date>(
    'project_filter_from_date',
    null,
  )

  const [toDate, setToDate] = useClientStorage<Date>(
    'project_filter_to_date',
    null,
  )

  const handleSetToDate = useCallback(
    (to: Date) => {
      const today = new Date()

      if (
        to.getDate() === today.getDate() &&
        to.getMonth() === today.getMonth() &&
        to.getFullYear() === today.getFullYear()
      ) {
        // if the chosen date is today, we set the toDate to null, so that the filter includes all projects up to today
        // and tomorrow the filter will be set to tomorrow automatically
        setToDate(null)
      } else {
        setToDate(to)
      }
    },
    [setToDate],
  )

  const [projectStateFilters, setProjectStateFilters] = useClientStorage<
    ProjectStateAsString[]
  >('project-state-filters', [])

  const handleProjectStateFilter = useCallback(
    (selectedProjectStates: ProjectStateAsString[]) => {
      const uniqueStates = uniq(selectedProjectStates)
      const includesCanceled = uniqueStates.includes(
        ProjectStateAsString.CANCELLED,
      )

      _setShowCanceledProjects((currentValue) =>
        currentValue === includesCanceled ? currentValue : includesCanceled,
      )
      setProjectStateFilters((currentFilters) =>
        isEqual(currentFilters, uniqueStates) ? currentFilters : uniqueStates,
      )
    },
    [_setShowCanceledProjects, setProjectStateFilters],
  )

  const validateDateRange = useCallback((from: Date, to: Date) => {
    let isValid = false
    const toDateObject = to ? new Date(to) : null
    const fromDateObject = from ? new Date(from) : null

    if (!toDateObject && !fromDateObject) {
      return true
    }

    if (toDateObject && toDateObject <= new Date()) {
      isValid = true
    }

    if (isValid && fromDateObject) {
      if (fromDateObject > toDateObject) {
        isValid = false
      }
    }

    return isValid
  }, [])

  return (
    <ProjectFilterContext
      value={{
        onlyMyProjects,
        setOnlyMyProjects,
        showCanceledProjects,
        setShowCanceledProjects: _setShowCanceledProjects,
        onlyCreatedInPortal,
        setOnlyCreatedInPortal,
        currentQuery,
        setCurrentQuery,
        fromDate,
        setFromDate,
        toDate,
        setToDate: handleSetToDate,
        projectStateFilters,
        handleProjectStateFilter,
        validateDateRange,
      }}
    >
      {props.children}
    </ProjectFilterContext>
  )
}
